// frontend/src/components/admin/AdminSecurity.tsx
/**
 * Admin Security - email two-factor authentication settings
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// 'enable' / 'disable': a code has been emailed and we're waiting for it
type Pending = 'enable' | 'disable' | null;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
});

const AdminSecurity: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    axios
      .get(`${API_URL}/admin/2fa/status`, authHeaders())
      .then((res) => setEnabled(res.data.enabled))
      .catch(() => setError('Could not load two-factor status'));
  }, []);

  const sendCode = async (action: 'enable' | 'disable') => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await axios.post(`${API_URL}/admin/2fa/send-setup-code`, {}, authHeaders());
      setPending(action);
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pending) return;
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/admin/2fa/${pending}`, { code }, authHeaders());
      setEnabled(res.data.enabled);
      setPending(null);
      setCode('');
      setNotice(res.data.enabled ? 'Two-factor authentication is on.' : 'Two-factor authentication is off.');
    } catch (err: any) {
      setCode('');
      setError(err.response?.data?.message || 'Incorrect or expired code.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setPending(null);
    setCode('');
    setError('');
  };

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5" />
        Security
      </h2>

      {enabled === null && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {enabled !== null && !pending && (
        <div className="space-y-4">
          {enabled ? (
            <>
              <p className="flex items-center gap-2 text-green-700 font-medium">
                <CheckCircle className="w-5 h-5" />
                Two-factor authentication is on.
              </p>
              <p className="text-sm text-gray-600">
                Each login requires a code sent to your email.
              </p>
              <button
                onClick={() => sendCode('disable')}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {busy ? 'Sending code…' : 'Disable'}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Require a code sent to your email each time you log in.
              </p>
              <button
                onClick={() => sendCode('enable')}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Sending code…' : 'Enable Two-Factor Authentication (email)'}
              </button>
            </>
          )}
        </div>
      )}

      {pending && (
        <form onSubmit={submitCode} className="space-y-4 max-w-sm">
          <p className="text-sm text-gray-700">
            Enter the code we just emailed you
            {pending === 'disable' ? ' to turn off two-factor authentication.' : '.'}
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg tracking-widest"
            autoFocus
          />
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy}
              className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                pending === 'disable' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {busy ? 'Checking…' : pending === 'disable' ? 'Confirm disable' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => sendCode(pending)}
              disabled={busy}
              className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
            >
              Resend code
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {notice && !pending && <p className="mt-4 text-sm text-green-700">{notice}</p>}

      {error && (
        <p className="mt-4 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
};

export default AdminSecurity;
